import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { staffAPI, type StaffDetail } from "@/lib/staff-api";
import { queryKeys } from "@/lib/query-keys";
import { useToast } from "@/components/ui/use-toast";

export const useStaffList = (params?: {
  page?: number;
  role?: string;
  search?: string;
}) => {
  return useQuery({
    queryKey: queryKeys.staff.list(params),
    queryFn: () => staffAPI.listStaff(params),
    retry: false,
  });
};

export const useStaffRoles = () => {
  return useQuery({
    queryKey: queryKeys.staff.roles(),
    queryFn: () => staffAPI.listRoles(),
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
};

export const useToggleStaffStatus = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({
      staffId,
      isActive,
    }: {
      staffId: string;
      isActive: boolean;
    }) => staffAPI.toggleStaffStatus(staffId, isActive),
    onSuccess: (_data, { staffId, isActive }) => {
      queryClient.setQueryData<StaffDetail>(
        queryKeys.staff.detail(staffId),
        (old) => (old ? { ...old, is_active: isActive } : old)
      );
      // Mark all staff queries stale without immediately refetching the detail —
      // the setQueryData above already gives the correct optimistic value.
      queryClient.invalidateQueries({
        queryKey: queryKeys.staff.all,
        refetchType: "none",
      });
      toast({
        title: "Status updated",
        description: "Staff member status has been updated successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: error?.status === 403 ? "Access Denied" : "Error",
        description:
          error?.status === 403
            ? "Your permissions no longer allow staff status changes."
            : error?.message ||
              "Failed to update staff status. Please try again.",
        variant: "destructive",
      });
    },
  });
};
